import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, scrollToId } from "@/data/site";
import PayDialog from "@/components/PayDialog";

const EASE = [0.16, 1, 0.3, 1];

export default function Services() {
  const [payService, setPayService] = useState(null);

  return (
    <section id="servicios" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-4 font-mono-sig text-xs font-bold uppercase tracking-[0.3em] text-[#1D4ED8]">
              ( 01 ) — Qué hacemos
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              Servicios & tarifas
              <br />
              <span className="text-outline">sin letra pequeña.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-slate-500">
            Precios cerrados desde el primer día. Elige lo que tu negocio necesita hoy y escala cuando quieras.
          </p>
        </div>

        <div className="border-t border-slate-200">
          {SERVICES.map((s, idx) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE, delay: idx * 0.06 }}
              className="group relative grid cursor-pointer grid-cols-1 gap-6 border-b border-slate-200 py-10 transition-colors duration-500 hover:bg-white md:grid-cols-12 md:items-center md:px-6"
              onClick={() => scrollToId("#contacto")}
            >
              {s.popular && (
                <span className="absolute right-4 top-4 rounded-full bg-[#FF9F00] px-3 py-1 font-mono-sig text-[10px] font-bold uppercase tracking-widest text-slate-950 md:right-6">
                  Más pedido
                </span>
              )}
              <div className="md:col-span-1">
                <span className="font-mono-sig text-sm font-bold text-slate-300 transition-colors group-hover:text-[#00F0FF]">
                  {s.num}
                </span>
              </div>
              <div className="md:col-span-4">
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 transition-transform duration-500 group-hover:translate-x-2">
                  {s.name}
                </h3>
                <p className="mt-1 text-sm text-slate-500">{s.tagline}</p>
              </div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:col-span-4">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#1D4ED8]" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between gap-4 md:col-span-3 md:justify-end">
                <div className="md:text-right">
                  <p className="font-display text-2xl font-extrabold text-slate-900">{s.priceLabel}</p>
                  <p className="font-mono-sig text-[10px] uppercase tracking-widest text-slate-400">{s.priceNote}</p>
                </div>
                <button
                  data-testid={`service-pay-${s.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPayService(s);
                  }}
                  className="shrink-0 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 transition-colors hover:border-[#1D4ED8] hover:bg-[#1D4ED8] hover:text-white"
                >
                  Pagar
                </button>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-300 transition-all duration-500 group-hover:border-slate-900 group-hover:bg-slate-900 group-hover:text-[#00F0FF]">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <PayDialog
        open={!!payService}
        onOpenChange={() => setPayService(null)}
        defaultAmount={payService?.price ?? 50}
        description={payService ? `Señal · ${payService.name} — Socify Marketing` : "Pago a Socify Marketing"}
      />
    </section>
  );
}
